import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import FeaturedImage from '@/components/FeaturedImage';
import { useSite, useWhatsappUrl } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';

const HOME_CARDS = [
    {
        eyebrow: 'FLOREAL',
        title: 'atelier',
        desc: "Bouquet, composizioni e piante d’autore. Ogni stagione, il suo carattere.",
        href: '/atelier',
        slot: 'home_card_fiori',
        testid: TEST_IDS.homeCardFiori,
    },
    {
        eyebrow: 'FLOREAL',
        title: 'matrimoni',
        desc: 'Allestimenti floreali su misura, dalla cerimonia al ricevimento. Cura, armonia e consulenza dedicata.',
        href: '/matrimoni',
        slot: 'home_card_matrimoni',
        testid: TEST_IDS.homeCardMatrimoni,
    },
    {
        eyebrow: 'FLOREAL',
        title: 'workshop',
        desc: "Esperienze esclusive per imparare l’arte del fiore attraverso gesti semplici e creativi.",
        href: '/workshop',
        slot: 'home_card_workshop',
        testid: TEST_IDS.homeCardWorkshop,
    },
    {
        eyebrow: 'FLOREAL',
        title: 'giardini',
        desc: 'Progettazione e cura di spazi verdi privati. Natura, equilibrio e bellezza in ogni stagione.',
        href: '/giardini',
        slot: 'home_card_giardini',
        testid: TEST_IDS.homeCardGiardini,
    },
];

export default function Home() {
    const { t } = useSite();
    const whatsappUrl = useWhatsappUrl();
    useSeo({
        title: `${SITE_NAME} — Atelier floreale a Lomazzo, Como`,
        description:
            'Floreal Atelier del Fiore a Lomazzo: composizioni floreali, allestimenti ed emozioni su misura. Atelier, matrimoni, workshop e giardini.',
    });

    const HOME_CARDS_DYN = [
        { eyebrow: 'FLOREAL', title: t('home.card_atelier.title', 'atelier'), desc: t('home.card_atelier.desc', HOME_CARDS[0].desc), href: '/atelier', slot: 'home_card_fiori', testid: TEST_IDS.homeCardFiori },
        { eyebrow: 'FLOREAL', title: t('home.card_matrimoni.title', 'matrimoni'), desc: t('home.card_matrimoni.desc', HOME_CARDS[1].desc), href: '/matrimoni', slot: 'home_card_matrimoni', testid: TEST_IDS.homeCardMatrimoni },
        { eyebrow: 'FLOREAL', title: t('home.card_workshop.title', 'workshop'), desc: t('home.card_workshop.desc', HOME_CARDS[2].desc), href: '/workshop', slot: 'home_card_workshop', testid: TEST_IDS.homeCardWorkshop },
        { eyebrow: 'FLOREAL', title: t('home.card_giardini.title', 'giardini'), desc: t('home.card_giardini.desc', HOME_CARDS[3].desc), href: '/giardini', slot: 'home_card_giardini', testid: TEST_IDS.homeCardGiardini },
    ];

    return (
        <>
            {/* HERO — image with dark overlay + white text, centered vertically */}
            <section
                data-testid={TEST_IDS.heroSection}
                className="relative w-full min-h-[100vh] flex items-center justify-center pt-20 pb-16 overflow-hidden"
            >
                <FeaturedImage
                    slot="hero_home"
                    alt="Composizione floreale dell'atelier Floreal"
                    priority
                    className="absolute inset-0 w-full h-full"
                    imgClassName="absolute inset-0 w-full h-full object-cover object-[70%_center] sm:object-[65%_center]"
                    fallbackVariant="hero"
                    fallbackLabel="Atelier del Fiore"
                />
                {/* Dark overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55 pointer-events-none" />
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative container-atelier z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-4xl mx-auto"
                    >
                        <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.05] tracking-tight text-balance drop-shadow-[0_2px_24px_rgba(0,0,0,0.35)] whitespace-pre-line">
                            {t('home.hero.title', 'Floreal\nAtelier del fiore')}
                        </h1>
                        <p className="mt-8 sm:mt-10 text-lg sm:text-xl lg:text-2xl text-atelier-ivory/95 max-w-3xl mx-auto leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]">
                            {t('home.hero.description', 'Composizioni floreali, allestimenti ed emozioni su misura per trasformare ogni occasione in un ricordo da vivere.')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 4 CARDS — "Quattro modi per vivere Floreal" — no lead (cleaner) */}
            <Section title={t('home.cards.section_title', 'Quattro modi per vivere Floreal')}>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6">
                    {HOME_CARDS_DYN.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.45, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                            className="h-full"
                        >
                            <Link to={c.href} data-testid={c.testid} className="block group h-full">
                                <Card className="overflow-hidden border-border bg-card rounded-xl shadow-none hover:shadow-md-atelier transition-shadow duration-300 h-full flex flex-col">
                                    <FeaturedImage
                                        slot={c.slot}
                                        alt={c.title}
                                        className="aspect-[4/5]"
                                        imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                        fallbackLabel={c.title}
                                    />
                                    <CardContent className="p-6 flex-1 flex flex-col">
                                        <p className="eyebrow tracking-[0.32em] text-atelier-bronze-soft">{c.eyebrow}</p>
                                        <h3 className="mt-2 font-serif italic text-3xl tracking-tight leading-none capitalize">{c.title}</h3>
                                        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                                    </CardContent>
                                </Card>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* CTA Final */}
            <Section className="py-16 sm:py-20">
                <div className="rounded-xl bg-atelier-ivory border border-atelier-sage/25 px-6 sm:px-10 py-12 sm:py-16 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl mx-auto leading-[1.1]">
                        {t('home.cta.title', 'Entra nel mondo Floreal')}
                    </h3>
                    <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        {t('home.cta.description', 'Passa a trovarci in atelier e lasciati ispirare da fiori, piante e progetti pensati su misura.')}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" className="h-12 px-6" data-testid={TEST_IDS.homeCtaInfoCta}>
                            <Link to="/contatti#form">{t('home.cta.btn_info', 'Richiedi informazioni')}</Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" className="h-12 px-6 border-foreground/20 bg-background hover:bg-secondary" data-testid={TEST_IDS.homeCtaWhatsappCta}>
                            <a href={whatsappUrl} target="_blank" rel="noreferrer">
                                <MessageCircle className="h-4 w-4 mr-2" /> {t('home.cta.btn_whatsapp', 'Scrivici su WhatsApp')}
                            </a>
                        </Button>
                    </div>
                </div>
            </Section>
        </>
    );
}
